/**
 *   Created by Arif Topcu in 2017
 *
 *   - Original source credit: https://github.com/KyleAMathews/deepmerge
 *   - Modified array merge algo so arrays deep merge based on index instead of simple concat
 *
 */

(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory()
    : typeof define === 'function' && define.amd ? define(factory)
      : (global.deepmerge = factory());
}(this, (() => {
  const isMergeableObject = function isMergeableObject(value) {
    return isNonNullObject(value)
            && !isSpecial(value);
  };

  function isNonNullObject(value) {
    return !!value && typeof value === 'object';
  }

  function isSpecial(value) {
    const stringValue = Object.prototype.toString.call(value);

    return stringValue === '[object RegExp]'
            || stringValue === '[object Date]'
            || isReactElement(value);
  }

  // see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25
  const canUseSymbol = typeof Symbol === 'function' && Symbol.for;
  const REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

  function isReactElement(value) {
    return value.$$typeof === REACT_ELEMENT_TYPE;
  }

  function emptyTarget(val) {
    return Array.isArray(val) ? [] : {};
  }

  function cloneUnlessOtherwiseSpecified(value, optionsArgument) {
    const clone = !optionsArgument || optionsArgument.clone !== false;

    return (clone && isMergeableObject(value))
      ? deepmerge(emptyTarget(value), value, optionsArgument)
      : value;
  }

  function _defaultArrayMerge(target, source, optionsArgument) {
    return target.concat(source).map(element => cloneUnlessOtherwiseSpecified(element, optionsArgument));
  }

  function defaultArrayMerge(target, source, optionsArgument) {
    const destination = target.slice();

    source.forEach((e, i) => {
      if (typeof destination[i] === 'undefined') {
        const cloneRequested = !optionsArgument || optionsArgument.clone !== false;
        const shouldClone = cloneRequested && isMergeableObject(e);
        destination[i] = shouldClone ? cloneUnlessOtherwiseSpecified(e, optionsArgument) : e;
      } else if (isMergeableObject(e)) {
        destination[i] = mergeObject(target[i], e, optionsArgument);
      } else if (target.indexOf(e) === -1) {
        destination.push(e);
      }
    });
    return destination;
  }

  function mergeObject(target, source, optionsArgument) {
    const destination = {};
    if (isMergeableObject(target)) {
      Object.keys(target).forEach((key) => {
        destination[key] = cloneUnlessOtherwiseSpecified(target[key], optionsArgument);
      });
    }
    Object.keys(source).forEach((key) => {
      if (!isMergeableObject(source[key]) || !target[key]) {
        destination[key] = cloneUnlessOtherwiseSpecified(source[key], optionsArgument);
      } else {
        destination[key] = deepmerge(target[key], source[key], optionsArgument);
      }
    });
    return destination;
  }

  function deepmerge(target, source, optionsArgument) {
    const sourceIsArray = Array.isArray(source);
    const targetIsArray = Array.isArray(target);
    const options = optionsArgument || { arrayMerge: defaultArrayMerge };
    const sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

    if (!sourceAndTargetTypesMatch) {
      return cloneUnlessOtherwiseSpecified(source, optionsArgument);
    } if (sourceIsArray) {
      const arrayMerge = options.arrayMerge || defaultArrayMerge;
      return arrayMerge(target, source, optionsArgument);
    }
    return mergeObject(target, source, optionsArgument);
  }

  deepmerge.all = function deepmergeAll(array, optionsArgument) {
    if (!Array.isArray(array)) {
      throw new Error('first argument should be an array');
    }

    return array.reduce((prev, next) => deepmerge(prev, next, optionsArgument), {});
  };

  const deepmerge_1 = deepmerge;

  return deepmerge_1;
})));
